import { z } from 'zod';

import type { userSchema } from '~/components/forms/adminUser/validations';
import { type UserRoleValue } from '~/features/auth/roles';

export type ApiErrorResponse = {
  code: number;
  errors: { error: string; key: string }[];
  details: string;
};

export type LoginRequest = {
  email: string;
} & {
  password: string;
  consent?: {
    id: string;
    action: string;
  }[];
};
type Consent = { accepted: boolean; at: number };
type Patient = {
  firstName: string;
  lastName: string;
  email: string;
  phone: string;
  dateOfBirth: string;
  language: string;
  country: string;
  consents?: Record<string, Consent>;
};

export type UserRole = UserRoleValue;

export type AdminUser = {
  id: number;
  firstName: string;
  lastName: string;
  lastLogin?: string;
  email: string;
  phone?: string;
  departments: Department[];
  mainDepartment?: Department;
  role: UserRole;
  deletedAt?: string;
};

export type LoginResponse = {
  accessToken: string;
  tokenType: string;
  transmissionRules?: {
    backOff: number;
    backOffInitial: number;
    maxEvents: number;
    rules: {
      key: string;
      values: number[];
    }[];
    version: string;
    waitSeconds: number;
  };
  include: {
    patient: Patient;
  };
};

export type ErrorResponse = {
  message: string;
};

type Pagination = {
  pageSize: number;
  totalPages: number;
  page: number;
  lastSync: string;
};

export type PatientSort =
  | 'lastName'
  | 'firstName'
  | 'dateOfBirth'
  | 'phoneNumberLast4Digits'
  | 'alertsLast24hours'
  | 'lactate'
  | 'status'
  | 'createdAt';

export type DepartmentSort =
  | 'departmentId'
  | 'department'
  | 'admins'
  | 'IDN'
  | 'IDNId';

export type IDNSort = 'IDN' | 'IDNId';

export type AdminUserSort =
  | 'firstName'
  | 'lastName'
  | 'lastLogin'
  | 'email'
  | 'mainDepartment'
  | 'role';

export type AdminUserFilters = {
  role?: UserRole[];
  departmentId?: string;
  IDNId?: string;
};

export type PatientsFilters = {
  departmentId?: string;
  IDNId?: string;
  createdAfter?: string;
};

export type SearchRequest<TSort, TFilter = undefined> = {
  page?: number;
  sort?: TSort;
  sortDir?: 'asc' | 'desc';
  search?: string;
  filters?: TFilter;
};

export type DepartmentRequest = {
  departmentId?: string;
};

export type IDN = {
  IDN: string;
  IDNId: string;
  address: Address;
  phone: string;
};

export type Department = {
  IDN: string;
  IDNId: string;
  departmentId: string;
  department: string;
  admins: string[];
};

export type AlertSettings = {
  alertPhone: string;
  highLactateThreshold: number;
  period: number;
  shouldDisplayInApp: boolean;
};

export type Address = {
  mainLine: string;
  secondaryLine?: string;
  city: string;
  state: string;
  zipCode: string;
  country: string;
};

export type DepartmentDetails = Department & {
  address: Address;
  phone: string;
  dateFormat: string;
  alertsSettings: AlertSettings;
};

export type SearchResponse<T> = {
  data: T[];
  totalEntries: number;
} & Pagination;

type AlertState = 'muted';

export const SENSOR_STATUS = {
  ConnectionRequest: 'connectionRequest',
  ActiveEndingSoon: 'activeEndingSoon',
  ActiveNoData: 'activeNoData',
  InactiveActivationPending: 'inactiveActivationPending',
  Active: 'active',
  ActiveWarmingUp: 'activeWarmingUp',
  InactiveSensorEnded: 'inactiveSensorEnded',
  InactiveLastData: 'inactiveLastData',
} as const;

export type SensorStatus = (typeof SENSOR_STATUS)[keyof typeof SENSOR_STATUS];

export type SensorState = {
  status: SensorStatus;
  timestamp?: string;
};

export type PatientData = {
  id: string;
  firstName: string;
  lastName: string;
  phoneNumberLast4Digits: string;
  dateOfBirth: string;
  alertsLast24hours: null | number;
  alertState?: AlertState;
  status: SensorState;
  lactate: number;
  createdAt: string;
  departments: Department[];
  IDN: IDN;
};

export type PatientSearchResponse = {
  patients: PatientData[];
  totalPatients: number;
} & Pagination;

export type PatientSearchVerifyRequest = {
  phone: string;
};

export type PatientSearchVerifyResponseFound = {
  matchType: 'phone' | 'department' | 'archived';
  id: string;
  firstName: string;
  lastName: string;
  dateOfBirth: string;
  mrn: string;
  email: string;
  phone: string;
};

export type PatientSearchVerifyResponse = {
  found: PatientSearchVerifyResponseFound[];
};

export type PatientCreateRequest = {
  firstName: string;
  lastName: string;
  email: string;
  phone: string;
  dateOfBirth: string;
  zipCode: string;
  departmentId: string;
  country: string;
  language: string;
  mrn: string;
  inviteBy: 'email';
  sendInvite: boolean;
  settings: {
    displayOn: boolean;
    alert?: {
      value: number;
      durationMin: number;
    };
  };
};

export type PatientCreateResponse = {
  id: string;
  firstName: string;
  lastName: string;
  email: string;
  phone: string;
};

type Language = 'en-US';

export type AdminUserCreateRequestBody = {
  user: z.infer<typeof userSchema> & {
    country: string;
    language: Language;
    idnId: string;
  };
};

export type AdminUserCreateRequest = z.infer<typeof userSchema>;

// TODO - update this type when the Cloud endpoint is updated
export type UserActivationDataRequest = {
  token: string;
};

export type UserActivationDataResponse = {
  id: string;
  firstName: string;
  lastName: string;
  email: string;
  phone: string;
  role: string;
};

export type UserActivationUpdateRequest = {
  id: string;
  phone: string;
  password: string;
};
export type UserActivationUpdateResponse = {
  id: string;
  firstName: string;
  lastName: string;
  email: string;
  phone: string;
  role: string;
};

export type UserConsentsRequest = {
  termsOfUse?: string;
  privacyNotice?: string;
};

export type UserConsentsResponse = {
  termsOfUse: Consent;
  privacyNotice: Consent;
};
